<template>
  <div
      class="canvas"
      ref="canvasRef"
      @wheel="$event => handleMousewheelCanvas($event)"
      @mousedown="$event => handleClickBlankArea($event)"
      v-contextmenu="contextmenus"
      v-click-outside="removeEditorAreaFocus"
      id="canvas"
  >
    <ElementCreateSelection
        v-if="creatingElement && !slideNanoid"
        @created="data => insertElementFromCreateSelection(data)"
    />
    <div
        class="viewport-wrapper"
        :style="{
        left: viewportStyles.left + 'px',
        top: '30px',
        ...backgroundStyle
      }"
    >
      <div class="operates">
        <AlignmentLine
            v-for="(line, index) in alignmentLines"
            :key="index"
            :type="line.type"
            :axis="line.axis"
            :length="line.length"
            :canvasScale="canvasScale"
        />
        <MultiSelectOperate
            v-if="activeElementIdList.length > 1"
            :elementList="elementList"
            :scaleMultiElement="scaleMultiElement"
        />
        <Operate
            v-for="element in elementList"
            :key="element.id"
            :elementInfo="element"
            :isSelected="activeElementIdList.includes(element.id)"
            :isActive="handleElementId === element.id"
            :isActiveGroupElement="activeGroupElementId === element.id"
            :isMultiSelect="activeElementIdList.length > 1"
            :rotateElement="rotateElement"
            :dragElement="dragElement"
            :scaleElement="scaleElement"
            :openLinkDialog="openLinkDialog"
            :dragLineElement="dragLineElement"
            :moveShapeKeypoint="moveShapeKeypoint"
            :selectElement="selectElement"
            v-show="!hiddenElementIdList.includes(element.id)"
        />
        <ViewportBackground id="viewport-background" @click="checkHtmlId($event)" />
      </div>

      <div
          class="viewport"
          ref="viewportRef"
          :style="{ transform: `scale(${canvasScale})`, height: viewportHeight }"
      >
        <MouseSelection
            v-if="mouseSelectionVisible"
            :top="mouseSelection.top"
            :left="mouseSelection.left"
            :width="mouseSelection.width"
            :height="mouseSelection.height"
            :quadrant="mouseSelectionQuadrant"
        />

        <ViewerElement
            v-if="headerElement"
            :key="headerElement.id"
            :elementIndex="0"
            :elementInfo="headerElement"
        />

        <EditableElement
            v-for="(element, index) in elementList.filter(element => !element.canvasHide)"
            :key="element.id"
            :elementInfo="element"
            :elementIndex="index + 5"
            :isMultiSelect="activeElementIdList.length > 1"
            :selectElement="selectElement"
            :openLinkDialog="openLinkDialog"
            v-show="!hiddenElementIdList.includes(element.id)"
            :style="{ top: `${masterSlideHeaderContainer}px` }"
        />

        <ViewerElement
            v-if="footerElement"
            :key="footerElement.id"
            :elementInfo="footerElement"
            :elementIndex="-1"
        />


      </div>
    </div>

    <div class="drag-mask" v-if="spaceKeyState && !slideNanoid"></div>

    <Ruler :viewportStyles="viewportStyles" v-if="showRuler && !slideNanoid" />

    <Dialog v-model:visible="backgroundDialogVisible" modal header="Slide Background" :style="{ width: '50rem' }">
      <SlideBackgroundChooser></SlideBackgroundChooser>
    </Dialog>

    <Modal
        v-model:visible="linkDialogVisible"
        :footer="null"
        centered
        :width="540"
        destroyOnClose

    >
      <LinkDialog @close="linkDialogVisible = false" />
    </Modal>


    <ThemeEditor v-model:dialogVisible="themeDialogVisible"></ThemeEditor>

  </div>
</template>

<script lang="ts" setup>
import { nextTick, onMounted, onUnmounted, provide, ref, watch, watchEffect, computed } from 'vue'
import { throttle } from 'lodash'
import { storeToRefs } from 'pinia'
import {
  useMainStore,
  useSlidesStore,
  useKeyboardStore,
  useWidgetStore,
  useMasterSlidesStore,
  useSnapshotStore, useAuthStore
} from '@/store'
import { ContextmenuItem } from '@/components/Contextmenu/types'
import {PPTDragDropElement, PPTElement, SlideBackground} from '@/types/slides'
import { AlignmentLineProps } from '@/types/edit'
import { injectKeySlideScale } from '@/types/injectKey'
import { removeAllRanges } from '@/utils/selection'
import { KEYS } from '@/configs/hotkey'
import useViewportSize from './hooks/useViewportSize'
import useMouseSelection from './hooks/useMouseSelection'
import useDropImageOrText from './hooks/useDropImageOrText'
import useRotateElement from './hooks/useRotateElement'
import useScaleElement from './hooks/useScaleElement'
import useSelectElement from './hooks/useSelectElement'
import useDragElement from './hooks/useDragElement'
import useDragLineElement from './hooks/useDragLineElement'
import useMoveShapeKeypoint from './hooks/useMoveShapeKeypoint'
import useInsertFromCreateSelection from './hooks/useInsertFromCreateSelection'
import ThemeEditor from "@/components/ThemeEditor.vue";

import useDeleteElement from '@/hooks/useDeleteElement'
import useCopyAndPasteElement from '@/hooks/useCopyAndPasteElement'
import useScaleCanvas from '@/hooks/useScaleCanvas'
import useSlideHandler from '@/hooks/useSlideHandler'

import SlideBackgroundChooser from './SlideBackgroundChooser.vue'
import EditableElement from './EditableElement.vue'
import MouseSelection from './MouseSelection.vue'
import ViewportBackground from './ViewportBackground.vue'
import AlignmentLine from './AlignmentLine.vue'
import Ruler from './Ruler.vue'
import ElementCreateSelection from './ElementCreateSelection.vue'
import MultiSelectOperate from './Operate/MultiSelectOperate.vue'
import Operate from './Operate/index.vue'
import LinkDialog from './LinkDialog.vue'
import { Modal } from 'ant-design-vue'
import {nanoid} from "nanoid";
import useSlideBackgroundStyle from "@/hooks/useSlideBackgroundStyle";
import ViewerElement from "@/views/Viewer/Canvas/ViewerElement.vue";
import useFileService from "@/views/Editor/CanvasTool/hooks/useFileService";
import useMicroservice from "@/hooks/useMicroservice";
import {useToast} from "primevue/usetoast";
const {getFileById} = useFileService()
const slidesStore = useSlidesStore()
const masterSlidesStore = useMasterSlidesStore()
const { theme } = storeToRefs(useSlidesStore())
const toast = useToast();

const mainStore = useMainStore()
const {
  activeElementIdList,
  activeGroupElementId,
  handleElementId,
  hiddenElementIdList,
  editorAreaFocus,
  gridLineSize,
  showRuler,
  creatingElement,
  canvasScale,
  textFormatPainter,
  slideNanoid,
  themeDialogVisible
} = storeToRefs(mainStore)
const { currentSlide, slides } = storeToRefs(useSlidesStore())
const { ctrlKeyState, spaceKeyState } = storeToRefs(useKeyboardStore())
const widgetStore = useWidgetStore()
const { activeType, selectedDragDropId, selectedWidget, sectionSelected, subsectionSelected } = storeToRefs(widgetStore)
const snapshotStore = useSnapshotStore()

const background = computed<SlideBackground | undefined>(() => currentSlide.value?.background)
const { backgroundStyle } = useSlideBackgroundStyle(background)
const canvasRef = ref<HTMLElement>()
const { dragViewport, viewportStyles } = useViewportSize(canvasRef)



const checkHtmlId = (event) => {
  if (event.target.id === 'viewport-background' || event.target.id === 'canvas') {
    activeType.value = 'none'
    widgetStore.setActiveWidgets([])
    sectionSelected.value = false
    subsectionSelected.value = false
  }
}

const viewportWrapperWidth = computed(() => currentSlide.value.infiniteScroll ? viewportStyles.value.width * canvasScale.value + 20 + 'px': viewportStyles.value.width * canvasScale.value + 'px')
const viewportWrapperHeight = computed(() => currentSlide.value.infiniteScroll ? 'calc(100% - 50px)': viewportStyles.value.height * canvasScale.value + 'px')
const viewportHeight = computed(() => currentSlide.value.infiniteScroll ? 'auto': viewportStyles.value.height + 'px')
const viewportWrapperScroll = computed(() => currentSlide.value.infiniteScroll ? 'hidden scroll': 'visible')

const backgroundDialogVisible = ref(false)
const viewportRef = ref<HTMLElement>()
const alignmentLines = ref<AlignmentLineProps[]>([])

const linkDialogVisible = ref(false)
const openLinkDialog = () => linkDialogVisible.value = true

watch(handleElementId, () => {
  mainStore.setActiveGroupElementId('')
})
const elementList = ref<PPTElement[]>([])

const headerElement = ref()
const footerElement = ref()
const masterSlideHeaderContainer = ref()
const fontName = computed(() => `"${theme.value.fontName}"` ?? '')
const setLocalElementList = async () => {
  const masterSlideCheck = currentSlide.value ? currentSlide.value.masterSlideId : null;
  if (masterSlideCheck) {
    await convertToViewerElements()
  } else {
    resetViewerElement()

    const newList = currentSlide.value ? JSON.parse(JSON.stringify(currentSlide.value.elements)) : [];
    elementList.value = newList.filter(entry => entry.type !== 'dragdrop');
  }

  theme.value.themeColors.map((c, i) => document.documentElement.style.setProperty(`--theme-${i}`, c))
  document.documentElement.style.setProperty(`--presentation-font`, fontName.value)
}

const convertToViewerElements = async () => {
  resetViewerElement()

  const {masterSlideId} = currentSlide.value
  let stored = masterSlidesStore.masterSlides.find(item => item.masterSlideId === masterSlideId)

  if (!stored) {
    const [fetchedSlide] = await getFileById('main', masterSlideId)
    stored = { masterSlideId: masterSlideId, slide: fetchedSlide }
    masterSlidesStore.masterSlides.push(stored)
  }

  const slide = JSON.parse(JSON.stringify(stored.slide))
  const slideLayoutElement = slide.elements.shift()
  const layoutRows = slideLayoutElement.layout.children

  const headerRow = layoutRows[0];
  headerRow.subtype = 'header'
  const headerRowId = headerRow.children[0].id
  const headerDragDrop = slide.elements.find(item => item.id === headerRowId)
  const footerRow = layoutRows.length > 2 ? layoutRows[layoutRows.length - 1] : null;

  // Having 2 copies of footer - 1 for normal data for thumbnail and viewer, 1 for read only view in the editor
  const editorFooterRow = JSON.parse(JSON.stringify(footerRow))
  if (footerRow) {
    for (const slide of slides.value) {
      const targetIndex = slide.elements.findIndex(
          (element) => element.type === 'dragdrop' && element.id === footerRow.children[0].dragDropElement.id
      )
      if (targetIndex !== -1) {
        slide.elements.splice(targetIndex, 1);
        slide.elements.push(footerRow.children[0].dragDropElement);
      }
    }

    footerRow.subtype = 'footer'
  }

  const footerRowId = footerRow.children[0].id
  const footerDragDrop = slide.elements.find(item => item.id === footerRowId)

  headerElement.value = wrapRowInDummySlideLayoutElement(headerRow);
  masterSlideHeaderContainer.value = getContainerStyle(headerRow);

  if (editorFooterRow) {
    const footerTopPosition = viewportStyles.value.height - getContainerStyle(footerRow);
    footerElement.value = wrapRowInDummySlideLayoutElement(editorFooterRow, footerTopPosition);
  }

  currentSlide.value.elements = currentSlide.value.elements.filter(item => item.id !== headerDragDrop.id && (footerDragDrop === null || item.id !== footerDragDrop.id));

  const current = currentSlide.value ? currentSlide.value.elements : [];
  const currentSlideLayout = current.shift()
  updateFirstAndLast(currentSlideLayout.layout.children, headerRow, footerRow)
  current.unshift(currentSlideLayout)
  if (headerDragDrop) currentSlide.value.elements.push(headerDragDrop)
  if (footerDragDrop) currentSlide.value.elements.push(footerDragDrop)


  elementList.value = current.filter(entry => entry.type !== 'dragdrop');
}

const resetViewerElement = () => {
  headerElement.value = null
  footerElement.value = null
  masterSlideHeaderContainer.value = null
}

const updateFirstAndLast = (arr, header, footer) => {
  arr[0] = header
  if (arr.length > 1) {
    arr[arr.length-1]  = footer
  }
  return arr
}

const getContainerStyle = (rowObject) => {
  if (rowObject?.children?.[0]?.style?.containerHeight) {
    return rowObject.children[0].style.containerHeight;
  } else {
    return 0;
  }
};

const wrapRowInDummySlideLayoutElement = (row, top = 0) => {
  const content = ''
  const width = 1366
  const height = 0 // this isn't working, setting it to 20
  const left = 0
  const id = nanoid(10)

  return {
    type: 'slidelayout',
    id,
    left,
    top,
    width,
    height,
    content,
    rotate: 0,
    defaultFontName: 'Arial',
    defaultColor: theme.value.fontColor,
    layout: {
      'type': 'layout',
      'children': [row]
    }
  }
}

watchEffect(setLocalElementList)

useDropImageOrText(canvasRef)

const { mouseSelection, mouseSelectionVisible, mouseSelectionQuadrant, updateMouseSelection } = useMouseSelection(elementList, viewportRef)

const { dragElement } = useDragElement(elementList, alignmentLines, canvasScale)
const { dragLineElement } = useDragLineElement(elementList)
const { selectElement } = useSelectElement(elementList, dragElement)
const { scaleElement, scaleMultiElement } = useScaleElement(elementList, alignmentLines, canvasScale)
const { rotateElement } = useRotateElement(elementList, viewportRef)
const { moveShapeKeypoint } = useMoveShapeKeypoint(elementList, canvasScale)

const { deleteAllElements } = useDeleteElement()
const { pasteElement, copyElement } = useCopyAndPasteElement()
const { updateSlideIndex } = useSlideHandler()


// When the component is rendered, if there is an element focus, it needs to be cleared
// This situation exists: when there is a focus element, the show mode is entered, and when exiting, the original focus needs to be cleared (because the page may have been switched)
onMounted(async () => {
  if (activeElementIdList.value.length) {
    await nextTick(() => {
      mainStore.setActiveElementIdList([])
    })
  }
  await snapshotStore.initSnapshotDatabase()
})

// Click on the blank area of the canvas: clear the focus element, set the canvas focus, clear the text selection area, clear the formatting brush state
const handleClickBlankArea = (e: MouseEvent) => {
  if (activeElementIdList.value.length) mainStore.setActiveElementIdList([])
  checkHtmlId(e)

  if (!spaceKeyState.value) updateMouseSelection(e)
  else dragViewport(e)

  if (!editorAreaFocus.value) mainStore.setEditorareaFocus(true)
  if (textFormatPainter.value) mainStore.setTextFormatPainter(null)
  removeAllRanges()
}

// Clear the format painter state when the canvas is logged out
onUnmounted(() => {
  if (textFormatPainter.value) mainStore.setTextFormatPainter(null)
})

// Remove focus from canvas editing area
const removeEditorAreaFocus = () => {
  if (editorAreaFocus.value) mainStore.setEditorareaFocus(false)
}

// scroll mouse
const { scaleCanvas } = useScaleCanvas()
const throttleScaleCanvas = throttle(scaleCanvas, 100, { leading: true, trailing: false })
const throttleUpdateSlideIndex = throttle(updateSlideIndex, 1200, { leading: true, trailing: false })

const handleMousewheelCanvas = (e: WheelEvent) => {
  e.preventDefault()

  // While holding down the Ctrl key: zoom the canvas
  if (ctrlKeyState.value) {
    if (e.deltaY > 0) throttleScaleCanvas('-')
    else if (e.deltaY < 0) throttleScaleCanvas('+')
  }
  // page up and down
  else {
    if (e.deltaY > 0) throttleUpdateSlideIndex(KEYS.DOWN)
    else if (e.deltaY < 0) throttleUpdateSlideIndex(KEYS.UP)
  }
}

// Switch ruler
const toggleRuler = () => {
  mainStore.setRulerState(!showRuler.value)
}

const changeBackground = () => {
  backgroundDialogVisible.value = true
}
const element = computed(() => {
  return selectedWidget.value
}) as any | null

const duplicateWidget = () => {
  const dragDropElement: PPTDragDropElement = <PPTDragDropElement>slidesStore.getElementsByType('dragdrop').find((zone) => zone.id === selectedDragDropId.value)
  const reactiveJson = dragDropElement ? dragDropElement.widgets : []
  const elementId = element.value?.id

  if (reactiveJson) {
    const parentIndex = reactiveJson.findIndex(parent =>
        parent.children.some(child => child.id === elementId)
    )

    if (parentIndex !== -1) {
      const childIndex = reactiveJson[parentIndex].children.findIndex(child =>
          child.id === elementId
      )
      if (childIndex !== -1) {
        const originalWidget = reactiveJson[parentIndex].children[childIndex]
        const clonedWidget = { ...originalWidget, id: nanoid(10) }

        reactiveJson[parentIndex].children.splice(childIndex + 1, 0, clonedWidget)
        widgetStore.setActiveWidgets([clonedWidget])
      }
    }
  }

}

// Insert elements within the range drawn by the mouse
const { insertElementFromCreateSelection } = useInsertFromCreateSelection(viewportRef)

const contextmenus = (): ContextmenuItem[] => {

  return [
    {
      text: 'Duplicate',
      handler: duplicateWidget,
    },
    // {
    //   text: 'Copy',
    //   subText: 'Ctrl + C',
    //   handler: copyElement,
    // },
    // {
    //   text: 'Paste',
    //   subText: 'Ctrl + V',
    //   handler: pasteElement,
    // },
    {
      text: 'Delete All Elements',
      handler: deleteAllElements,
    },
    {
      text: 'Change Background',
      handler: changeBackground,
    },
  ]
}

provide(injectKeySlideScale, canvasScale)
</script>

<style lang="scss" scoped>
.canvas {
  height: 100%;
  user-select: none;
  overflow: hidden;
  background-color: $lightGray;
  position: relative;
}
.drag-mask {
  cursor: grab;
  @include absolute-0();
}
.viewport-wrapper {
  position: absolute;
  box-shadow: 0 0 15px 0 rgba(0, 0, 0, 0.1);
  width: v-bind(viewportWrapperWidth);
  height: v-bind(viewportWrapperHeight);
  overflow: v-bind(viewportWrapperScroll);
}
.viewport {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: 0 0;
}

::-webkit-scrollbar {
  background-color:#fff;
  width:16px
}

/* background of the scrollbar except button or resizer */
::-webkit-scrollbar-track {
  background-color:#fff
}
::-webkit-scrollbar-track:hover {
  background-color:#f4f4f4
}

/* scrollbar itself */
::-webkit-scrollbar-thumb {
  background-color:#babac0;
  border-radius:16px;
  border:5px solid #fff
}
::-webkit-scrollbar-thumb:hover {
  background-color:#a0a0a5;
  border:4px solid #f4f4f4
}
</style>
